import { useState } from 'react';
import { KeyRound, Loader2, Shield } from 'lucide-react';
import { useChangePasswordMutation } from '@/features/profile/api/useProfileQueries';
import { PasswordField } from './security/PasswordField';

export function SecurityTab() {
  const changePw = useChangePasswordMutation();
  const [current, setCurrent] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPw.length < 6 || newPw !== confirm) return;
    changePw.mutate(
      { currentPassword: current, newPassword: newPw, confirmPassword: confirm },
      {
        onSuccess: () => {
          setCurrent('');
          setNewPw('');
          setConfirm('');
        },
      },
    );
  };

  const mismatch = confirm.length > 0 && newPw !== confirm;

  return (
    <section className="max-w-lg rounded-2xl border border-[var(--dash-line)] bg-white p-5 text-left shadow-[0_4px_20px_-10px_rgba(16,38,73,0.08)]">
      <div className="mb-5 flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-[var(--dash-brand)]" />
        <h2 className="m-0 text-[15px] font-semibold text-[var(--dash-ink)]">
          Change password
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <PasswordField
          label="Current password"
          value={current}
          onChange={setCurrent}
          visible={showCurrent}
          onToggleVisible={() => setShowCurrent((v) => !v)}
          placeholder="Enter current password"
        />

        <PasswordField
          label="New password"
          value={newPw}
          onChange={setNewPw}
          visible={showNew}
          onToggleVisible={() => setShowNew((v) => !v)}
          minLength={6}
          placeholder="Enter new password"
        />

        <PasswordField
          label="Confirm new password"
          value={confirm}
          onChange={setConfirm}
          visible={showConfirm}
          onToggleVisible={() => setShowConfirm((v) => !v)}
          invalid={mismatch}
          errorText={mismatch ? "Passwords don't match" : undefined}
          placeholder="Enter confirm new password"
        />

        <button
          type="submit"
          disabled={changePw.isPending || mismatch || newPw.length < 6}
          className="mt-1 inline-flex items-center justify-center gap-2 self-start rounded-xl px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          style={{
            background:
              'linear-gradient(90deg, #8E58A6 0%, #5A4EA6 25%, #145E8C 50%, #007A78 75%, #33A65B 100%)',
          }}
        >
          {changePw.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Shield className="h-4 w-4" />
          )}
          Change password
        </button>
      </form>
    </section>
  );
}
